import { useCallback, useEffect, useState } from 'react';

// Row windowing vertical del grid del Planner (D6, R5, 2026-08-24 bis).
//
// Tras los índices O(1) por celda (5.1.1-5.1.3) el coste residual del Long Task
// del mount es **puro volumen de render**: React reconciliando las 312 celdas +
// ~1000 nodos de booking. La única palanca que baja ese suelo es renderizar
// menos filas. Este hook, dado el `ref` del contenedor de scroll, calcula qué
// filas caen dentro del viewport (+ overscan) y devuelve los `startIndex`/
// `endIndex` a montar más los dos *spacers* (`topPad`/`bottomPad`) que preservan
// el `scrollHeight` completo — con lo que el auto-scroll nativo de DnD y la
// geometría de scroll siguen intactos.
//
// `overscanTop` es un **invariante duro** atado al span máximo de card de la UI
// (selector `[1,2,3,4,6,8]`: 8 slots): una card se renderiza solo en su celda de
// inicio y desborda hacia abajo por posición absoluta, así que su fila de inicio
// puede quedar *por encima* del viewport mientras su cuerpo entra en él. Con
// `overscanTop = 8` la fila de inicio de cualquier card de ≤ 8 slots que intrusa
// en el viewport está siempre montada. `overscanBottom` puede ser pequeño (2).
//
// La cabecera *sticky* queda **fuera** de la ventana; se ignora su altura en el
// cálculo (se asume índice 0 en `scrollTop = 0`). Como la cabecera desplaza el
// contenido hacia abajo, ignorarla solo puede hacer que se calcule un
// `startIndex` mayor que el real (nunca menor) y un `endIndex` mayor o igual: el
// `overscanTop = 8` (≫ altura de cabecera / rowHeight) absorbe de sobra ese
// desfase, de modo que nunca se desmonta una fila visible.
export function useRowWindow(scrollRef, rowHeight, total, overscanTop, overscanBottom) {
  const compute = useCallback(() => {
    const el = scrollRef && scrollRef.current;
    const viewport = el ? el.clientHeight : 0;
    // Sin contenedor medible (no montado, jsdom sin layout): montar todo. Es el
    // fallback seguro — nunca oculta filas.
    if (!el || viewport <= 0 || rowHeight <= 0 || total <= 0) {
      return { startIndex: 0, endIndex: total, topPad: 0, bottomPad: 0 };
    }
    // Clamp scrollTop al rango válido del contenido ACTUAL antes de derivar
    // índices. Si `total` encoge (p.ej. el refetch de `center` trae menos horas)
    // mientras el usuario está desplazado hacia abajo, un `scrollTop` obsoleto
    // (mayor que el nuevo `scrollHeight`) produciría `startIndex > endIndex` → un
    // slice vacío + un `topPad` mayor que el contenido real, rompiendo la
    // invariante "se preserva la geometría completa del scroll". El navegador
    // re-acota `scrollTop` al nuevo máximo en el siguiente frame; hacemos lo mismo
    // aquí para que el render intermedio sea correcto (muestra el fondo del
    // contenido) en vez de una rejilla en blanco.
    const maxScrollTop = Math.max(0, total * rowHeight - viewport);
    const scrollTop = Math.min(Math.max(0, el.scrollTop || 0), maxScrollTop);
    const firstVisible = Math.floor(scrollTop / rowHeight);
    const lastVisible = Math.ceil((scrollTop + viewport) / rowHeight);
    const startIndex = Math.max(0, firstVisible - overscanTop);
    const endIndex = Math.min(total, lastVisible + overscanBottom);
    return {
      startIndex,
      endIndex,
      topPad: startIndex * rowHeight,
      bottomPad: Math.max(0, (total - endIndex) * rowHeight),
    };
  }, [scrollRef, rowHeight, total, overscanTop, overscanBottom]);

  const [range, setRange] = useState(compute);

  useEffect(() => {
    const el = scrollRef && scrollRef.current;
    setRange(compute());
    if (!el) return undefined;

    let rafId = null;
    const flush = () => { rafId = null; setRange(compute()); };
    const onScroll = () => {
      // Coalescer los scroll con requestAnimationFrame: un recálculo por frame.
      if (rafId != null) return;
      if (typeof requestAnimationFrame === 'function') {
        rafId = requestAnimationFrame(flush);
      } else {
        flush();
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => setRange(compute()));
      ro.observe(el);
    }

    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafId != null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(rafId);
      }
      if (ro) ro.disconnect();
    };
  }, [compute, scrollRef]);

  return range;
}
